import type { VacationOverviewDocument, VacationOverviewPeriod } from '../../models/vacationOverview.types';
import { toIsoDay } from '../overviewDates';
import { daysUntil } from '../vacationOverview';
import { EXPIRING_WINDOW_DAYS, TAKEN_RECENT_DAYS } from './constants';
import type { DisplayDate } from './panoramaDisplayDates';
import { pickTodayDate, pickStatusItems } from './panoramaDisplayDates';
import { pickRequestItems, isApprovalUrgent, openRequestSegments } from './panoramaRequestItems';
import { periodPhase } from './panoramaPeriodPhase';
import { periodDisplayDates, resolvePeriodStatus, type PeriodRange } from './panoramaPeriod';
import { differenceInCalendarDays } from 'date-fns';

export type Status =
	| 'expired'
	| 'on-vacation'
	| 'scheduled'
	| 'pending'
	| 'approval-urgent'
	| 'expiring-soon'
	| 'taken'
	| 'none'
	| 'acquiring';

export type { DisplayDate };

export interface PersonRow {
	id: string;
	collaboratorId: string;
	name: string;
	positionName: string | null;
	photoUrl: string | null;
	clientNames: string[];

	concessiveDeadline: string | null;
	daysToConcessive: number | null;
	expiredDays: number;

	status: Status;
	displayDates: DisplayDate[];

	requestStage: 'manager' | 'client' | 'dp' | 'correction' | null;
	requestStages: Array<'manager' | 'client' | 'dp' | 'correction'>;
	requestSince: string | null;
	nextOutDate: string | null;
}

function resolveStatus(
	doc: VacationOverviewDocument,
	referenceDate: Date,
	todayIso: string,
	concessiveDeadline: string | null,
	daysToConcessive: number | null,
	currentPeriod: VacationOverviewPeriod | null,
	nextScheduledPeriod: VacationOverviewPeriod | null,
	lastTakenPeriod: VacationOverviewPeriod | null,
	expiredDays: number,
	legalDeadline: string | null
): Status {
	if ((expiredDays ?? 0) > 0 || (legalDeadline && legalDeadline < todayIso)) {
		return 'expired';
	}

	if (currentPeriod) {
		return 'on-vacation';
	}

	if (doc.openRequest) {
		return isApprovalUrgent(doc.openRequest, referenceDate) ? 'approval-urgent' : 'pending';
	}

	if (nextScheduledPeriod) {
		return 'scheduled';
	}

	if (concessiveDeadline && daysToConcessive !== null && daysToConcessive <= EXPIRING_WINDOW_DAYS) {
		return 'expiring-soon';
	}

	if (lastTakenPeriod) {
		const daysSinceTaken = differenceInCalendarDays(referenceDate, new Date(lastTakenPeriod.endDate + 'T00:00:00'));
		if (daysSinceTaken >= 0 && daysSinceTaken <= TAKEN_RECENT_DAYS) {
			return 'taken';
		}
	}

	if (!concessiveDeadline) {
		return 'acquiring';
	}

	return 'none';
}

export const PERIOD_INDEPENDENT_STATUSES: Status[] = ['acquiring', 'expired'];

function getPeriodModeDisplayDates(
	status: Status,
	doc: VacationOverviewDocument,
	period: PeriodRange,
	referenceDate: Date,
	daysToConcessive: number | null
): DisplayDate[] {
	const todayIso = toIsoDay(referenceDate);
	const periodDates = periodDisplayDates(doc, period, todayIso);
	const todayDateItems = pickTodayDate(status, doc, referenceDate, daysToConcessive);

	if (status === 'on-vacation' || status === 'scheduled') {
		return periodDates;
	}

	if (status === 'expired' && (doc.balance?.expiredDays ?? 0) > 0) {
		const expiredDateItem = pickTodayDate('expired', doc, referenceDate, daysToConcessive);
		return [...periodDates, ...expiredDateItem];
	}

	if (status === 'pending' || status === 'approval-urgent') {
		return pickRequestItems(doc, referenceDate, status, daysToConcessive, period);
	}

	return [...periodDates, ...todayDateItems];
}

export interface BuildPeopleRowsOptions {
	includeApprovalDeadline?: boolean;
}

export function buildPeopleRows(
	documents: VacationOverviewDocument[],
	referenceDate: Date,
	period?: PeriodRange,
	options: BuildPeopleRowsOptions = {}
): PersonRow[] {
	const todayIso = toIsoDay(referenceDate);
	const rows: PersonRow[] = [];
	const activeBase = documents.filter(d => d.active && d.inBase);

	activeBase.forEach(doc => {
		const concessiveDeadline = doc.balance?.nextDeadline ?? null;
		const daysToConcessive = concessiveDeadline
			? daysUntil(referenceDate, new Date(concessiveDeadline + 'T00:00:00'))
			: null;

		const expiredDays = doc.balance?.expiredDays ?? 0;
		const legalDeadline = doc.balance?.nextLegalDeadline ?? null;

		const currentPeriod = doc.periods.find(p => periodPhase(p, todayIso) === 'current');
		const nextScheduledPeriod = doc.periods
			.filter(p => periodPhase(p, todayIso) === 'future')
			.sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
		const lastTakenPeriod = doc.periods
			.filter(p => periodPhase(p, todayIso) === 'past')
			.sort((a, b) => b.endDate.localeCompare(a.endDate))[0];

		const todayStatus = resolveStatus(
			doc,
			referenceDate,
			todayIso,
			concessiveDeadline,
			daysToConcessive,
			currentPeriod ?? null,
			nextScheduledPeriod ?? null,
			lastTakenPeriod ?? null,
			expiredDays,
			legalDeadline
		);

		let displayDates: DisplayDate[];
		let status: Status = todayStatus;
		let inPeriod = true;

		if (period) {
			const periodStatus = resolvePeriodStatus(
				doc,
				todayStatus,
				period,
				todayIso,
				daysToConcessive,
				concessiveDeadline,
				options.includeApprovalDeadline ?? false
			);
			inPeriod = !!periodStatus;
			if (periodStatus) {
				status = periodStatus as Status;
				displayDates = getPeriodModeDisplayDates(status, doc, period, referenceDate, daysToConcessive);
			} else {
				displayDates = [];
			}
		} else {
			displayDates = pickStatusItems(status, doc, referenceDate, daysToConcessive);
		}

		let requestStage: 'manager' | 'client' | 'dp' | 'correction' | null = null;
		let requestStages: Array<'manager' | 'client' | 'dp' | 'correction'> = [];
		let requestSince: string | null = null;
		let nextOutDate: string | null = null;

		if (doc.openRequest) {
			const stageMap: Record<string, 'manager' | 'client' | 'dp' | 'correction'> = {
				PendingManager: 'manager',
				PendingClient: 'client',
				PendingDP: 'dp',
				PendingCorrection: 'correction',
			};
			requestStage = stageMap[doc.openRequest.status] ?? null;
			requestStages = requestStage ? [requestStage] : [];
			if (doc.openRequest.clientPending && requestStage !== 'client') {
				requestStages = [...requestStages, 'client'];
			}
			requestSince = doc.openRequest.stageSince ?? null;

			const segments = openRequestSegments(doc.openRequest);
			if (segments.length > 0) {
				const activeSegment = segments.find(seg => seg.endDate >= todayIso);
				if (activeSegment) {
					nextOutDate = activeSegment.startDate;
				}
			}
		}

		if (!period) {
			if (currentPeriod) {
				nextOutDate = currentPeriod.startDate;
			} else if (nextScheduledPeriod && !nextOutDate) {
				nextOutDate = nextScheduledPeriod.startDate;
			}
		}

		if (inPeriod) {
			rows.push({
				id: doc.collaboratorId,
				collaboratorId: doc.collaboratorId,
				name: doc.name,
				positionName: doc.positionName ?? null,
				photoUrl: doc.photoUrl ?? null,
				clientNames: doc.clientNames,

				concessiveDeadline,
				daysToConcessive,
				expiredDays,

				status,
				displayDates,

				requestStage,
				requestStages,
				requestSince,
				nextOutDate,
			});
		}
	});

	return rows;
}

export interface PeopleFilters {
	search?: string;
	statuses?: Status[];
	clients?: string[];
	periodFrom?: string | null;
	periodTo?: string | null;
	prazoMes?: string | null;
	stages?: Array<'manager' | 'client' | 'dp' | 'correction'> | null;
}

export function filterPeopleRows(rows: PersonRow[], filters: PeopleFilters): PersonRow[] {
	return rows.filter(row => {
		if (filters.search && filters.search.trim()) {
			const searchLower = filters.search.toLowerCase();
			if (!row.name.toLowerCase().includes(searchLower)) {
				return false;
			}
		}

		if (filters.statuses && filters.statuses.length > 0) {
			if (!filters.statuses.includes(row.status)) {
				return false;
			}
		}

		if (filters.clients && filters.clients.length > 0) {
			const hasClient = filters.clients.some(c => row.clientNames.includes(c));
			if (!hasClient) return false;
		}

		if (filters.stages && filters.stages.length > 0) {
			if (!row.requestStages.some(stage => filters.stages!.includes(stage))) {
				return false;
			}
		}

		if (filters.prazoMes) {
			if (!row.concessiveDeadline || !row.concessiveDeadline.startsWith(filters.prazoMes)) {
				return false;
			}
		}

		return true;
	});
}

export function countByStatus(rows: PersonRow[]): Record<Status | 'all', number> {
	const counts: Record<Status | 'all', number> = {
		all: rows.length,
		expired: 0,
		'on-vacation': 0,
		scheduled: 0,
		pending: 0,
		'approval-urgent': 0,
		'expiring-soon': 0,
		taken: 0,
		none: 0,
		acquiring: 0,
	};

	rows.forEach(row => {
		counts[row.status]++;
	});

	return counts;
}

